import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import {
// isGeneralFunctionExpression,
validateEmbeddedExpression, validateFunction, validateGlobalApi } from "./validations.js";
import { parseJsValue, parsePropValue } from "./parseJsValue.js";
import { parseChildren } from "./parseChildren.js";
import { parseUseResource } from "./parseUseResource.js";
import { parseUseContext } from "./parseUseContext.js";
import { parseIdentifierUse } from "./parseIdentifierUse.js";
import { parseUseImperativeHandle } from "./parseUseImperativeHandle.js";
export function parseComponent(fn, state, app, type, globalOptions) {
  if (!validateFunction(fn.node, state)) {
    return null;
  }
  var bindingMap = new Map();
  var component = {
    bindingMap: bindingMap,
    type: type,
    id: fn.node.id
  };
  var options = _objectSpread(_objectSpread({}, globalOptions), {}, {
    component: component
  });
  var params = fn.get("params");
  var templateRef;
  if (type === "template") {
    if (params.length > 2) {
      state.errors.push({
        message: "Component function can only have no more than 2 parameters, received ".concat(params.length),
        node: fn.node,
        severity: "error"
      });
      return null;
    }
    if (params.length > 0) {
      var propParam = params[0];
      if (!propParam.isObjectPattern()) {
        state.errors.push({
          message: "Component function first parameter must be an object pattern, received ".concat(propParam.type),
          node: propParam.node,
          severity: "error"
        });
        return null;
      }
      var _iterator = _createForOfIteratorHelper(propParam.get("properties")),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var prop = _step.value;
          if (prop.isRestElement()) {
            state.errors.push({
              message: "Component function parameter rest element is not allowed",
              node: prop.node,
              severity: "error"
            });
            return null;
          }
          var propNode = prop.node;
          if (propNode.computed || !propNode.shorthand) {
            state.errors.push({
              message: "Component function parameter properties must be shorthand and not computed",
              node: prop.node,
              severity: "error"
            });
            return null;
          }
          var key = prop.get("key");
          var value = prop.get("value");
          if (!key.isIdentifier()) {
            state.errors.push({
              message: "Component function parameter property key must be an identifier, received ".concat(key.type),
              node: key.node,
              severity: "error"
            });
            return null;
          }
          var varName = key.node.name;
          var isEventHandler = /^on[A-Z]/.test(varName);
          if (isEventHandler) {
            if (!value.isIdentifier()) {
              state.errors.push({
                message: "Event handler parameter \"".concat(varName, "\" must be an identifier, received ").concat(value.type),
                node: value.node,
                severity: "error"
              });
              return null;
            }
            bindingMap.set(value.node, {
              id: value.node,
              kind: "eventHandler"
            });
          } else {
            var bindingId = void 0;
            var initialValue = void 0;
            if (value.isAssignmentPattern()) {
              var left = value.get("left");
              if (left.isIdentifier()) {
                bindingId = left.node;
                initialValue = parseJsValue(value.get("right"), state, app, options);
              }
            } else if (value.isIdentifier()) {
              bindingId = value.node;
            }
            if (!bindingId) {
              state.errors.push({
                message: "Component function parameter property value must be an identifier or assignment pattern, received ".concat(value.type),
                node: value.node,
                severity: "error"
              });
              continue;
            }
            var paramBinding = {
              id: bindingId,
              kind: "param",
              initialValue: initialValue
            };
            bindingMap.set(bindingId, paramBinding);
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
    if (params.length > 1) {
      var refParam = params[1];
      if (!refParam.isIdentifier()) {
        state.errors.push({
          message: "Component function second parameter must be an identifier for ref, received ".concat(refParam.type),
          node: refParam.node,
          severity: "error"
        });
        return null;
      }
      templateRef = refParam;
    }
  } else if (params.length > 0) {
    state.errors.push({
      message: "Page function cannot have parameters, received ".concat(params.length),
      node: params[0].node,
      severity: "error"
    });
    return null;
  }
  var prevSlot;
  var prevParent = component;
  var stmts = fn.get("body").get("body");
  var _iterator2 = _createForOfIteratorHelper(stmts),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var stmt = _step2.value;
      if (stmt.isVariableDeclaration()) {
        if (stmt.node.kind !== "const") {
          state.errors.push({
            message: "Only \"const\" variable declaration is allowed, received \"".concat(stmt.node.kind, "\""),
            node: stmt.node,
            severity: "error"
          });
          continue;
        }
        var _iterator3 = _createForOfIteratorHelper(stmt.get("declarations")),
          _step3;
        try {
          for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
            var decl = _step3.value;
            var init = decl.get("init");
            // Hooks
            if (init.isCallExpression()) {
              var callee = init.get("callee");
              if (callee.isIdentifier()) {
                var args = init.get("arguments");
                if (validateGlobalApi(callee, "useState")) {
                  var _declId = decl.get("id");
                  if (!_declId.isArrayPattern()) {
                    continue;
                  }
                  var elements = _declId.get("elements");
                  if (elements.length !== 2) {
                    state.errors.push({
                      message: "useState() destructuring must have exactly two elements, received ".concat(elements.length),
                      node: _declId.node,
                      severity: "error"
                    });
                    continue;
                  }
                  var stateVar = elements[0];
                  var setStateVar = elements[1];
                  if (!stateVar.isIdentifier() || !setStateVar.isIdentifier()) {
                    state.errors.push({
                      message: "useState() destructuring must have identifiers as elements, received ".concat(elements.map(function (el) {
                        return el.type;
                      }).join(", ")),
                      node: _declId.node,
                      severity: "error"
                    });
                    continue;
                  }
                  var stateInfo = {
                    id: stateVar.node,
                    kind: "state"
                  };
                  bindingMap.set(stateVar.node, stateInfo);
                  bindingMap.set(setStateVar.node, {
                    id: stateVar.node,
                    kind: "setState"
                  });
                  if (args.length > 0) {
                    stateInfo.initialValue = parseJsValue(args[0], state, app, options);
                  }
                  if (args.length > 1) {
                    state.errors.push({
                      message: "useState() only accepts at most one argument, received ".concat(args.length),
                      node: args[1].node,
                      severity: "warning"
                    });
                  }
                  continue;
                } else if (validateGlobalApi(callee, "useResource")) {
                  var bindings = parseUseResource(decl, args, state, app, options);
                  if (bindings) {
                    var _iterator4 = _createForOfIteratorHelper(bindings),
                      _step4;
                    try {
                      for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
                        var _binding = _step4.value;
                        bindingMap.set(_binding.id, _binding);
                      }
                    } catch (err) {
                      _iterator4.e(err);
                    } finally {
                      _iterator4.f();
                    }
                  }
                  continue;
                } else if (validateGlobalApi(callee, "useRef")) {
                  var _declId2 = decl.get("id");
                  if (!_declId2.isIdentifier()) {
                    state.errors.push({
                      message: "useRef() must be assigned to an identifier, received ".concat(_declId2.type),
                      node: _declId2.node,
                      severity: "error"
                    });
                    continue;
                  }
                  if (args.length > 1) {
                    state.errors.push({
                      message: "useRef() only accepts at most one argument, received ".concat(args.length),
                      node: args[1].node,
                      severity: "warning"
                    });
                  }
                  var firstArg = args[0];
                  if (firstArg && !firstArg.isNullLiteral()) {
                    state.errors.push({
                      message: "useRef() first argument must be null, received ".concat(firstArg.type),
                      node: firstArg.node,
                      severity: "warning"
                    });
                  }
                  bindingMap.set(_declId2.node, {
                    id: _declId2.node,
                    kind: "ref"
                  });
                  continue;
                } else {
                  var identifierUse = parseIdentifierUse(decl, callee, args, state);
                  if (identifierUse === false) {
                    continue;
                  }
                  if (identifierUse) {
                    bindingMap.set(identifierUse[0], identifierUse[1]);
                    continue;
                  }
                }
                if (validateGlobalApi(callee, "useContext")) {
                  var _bindings = parseUseContext(decl, args, state, app, options);
                  if (_bindings) {
                    var _iterator5 = _createForOfIteratorHelper(_bindings),
                      _step5;
                    try {
                      for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
                        var _binding2 = _step5.value;
                        bindingMap.set(_binding2.id, _binding2);
                      }
                    } catch (err) {
                      _iterator5.e(err);
                    } finally {
                      _iterator5.f();
                    }
                  }
                  continue;
                }
              }
            }

            // Normal variable
            var declId = decl.get("id");
            if (!declId.isIdentifier()) {
              state.errors.push({
                message: "Expect an identifier as the variable name, received ".concat(declId.type),
                node: declId.node,
                severity: "error"
              });
              continue;
            }
            var binding = {
              id: declId.node,
              kind: "constant"
            };
            bindingMap.set(declId.node, binding);
            if (init.node) {
              binding.initialValue = parseJsValue(init, state, app, options);
            }
          }
        } catch (err) {
          _iterator3.e(err);
        } finally {
          _iterator3.f();
        }
        continue;
      }
      if (stmt.isReturnStatement()) {
        var children = parseReturnStatement(stmt, state, app, options);
        if (children) {
          var _prevParent, _prevParent$children, _prevParent$children2;
          (_prevParent$children = (_prevParent = prevParent).children) !== null && _prevParent$children !== void 0 ? _prevParent$children : _prevParent.children = [];
          (_prevParent$children2 = prevParent.children).push.apply(_prevParent$children2, _toConsumableArray(prevSlot ? children.map(function (child) {
            return _objectSpread(_objectSpread({}, child), {}, {
              slot: prevSlot
            });
          }) : children));
        }
        break;
      }
      if (stmt.isIfStatement()) {
        var _prevParent2, _prevParent2$children;
        var test = stmt.get("test");
        if (!validateEmbeddedExpression(test.node, state)) {
          return null;
        }
        var consequent = stmt.get("consequent");
        var alternate = stmt.get("alternate");
        var consequentChildren = parseIfStatementChildren(consequent, state, app, options);
        var alternateChildren = alternate ? parseIfStatementChildren(alternate, state, app, options) : null;
        (_prevParent2$children = (_prevParent2 = prevParent).children) !== null && _prevParent2$children !== void 0 ? _prevParent2$children : _prevParent2.children = [];
        var ifComponent = {
          name: "If",
          properties: {
            dataSource: parsePropValue(test, state, app, _objectSpread(_objectSpread({}, options), {}, {
              modifier: "="
            }))
          },
          children: [].concat(_toConsumableArray(consequentChildren !== null && consequentChildren !== void 0 ? consequentChildren : []), _toConsumableArray((alternateChildren !== null && alternateChildren !== void 0 ? alternateChildren : []).map(function (child) {
            return _objectSpread(_objectSpread({}, child), {}, {
              slot: "else"
            });
          }))),
          slot: prevSlot
        };
        prevParent.children.push(ifComponent);
        prevParent = ifComponent;
        if (consequentChildren) {
          prevSlot = "else";
          if (alternateChildren) {
            break;
          }
        }
        continue;
      }
      if (stmt.isExpressionStatement()) {
        var expr = stmt.get("expression");
        if (expr.isCallExpression()) {
          var _callee = expr.get("callee");
          if (!_callee.isIdentifier()) {
            state.errors.push({
              message: "Unsupported call expression callee type: ".concat(_callee.type),
              node: _callee.node,
              severity: "error"
            });
            continue;
          }
          // if (validateGlobalApi(callee, "useEffect")) {
          //   const args = expr.get("arguments");
          //   if (args.length !== 2) {
          //     state.errors.push({
          //       message: `useEffect() requires exactly 2 arguments, received ${args.length}`,
          //       node: expr.node,
          //       severity: "error",
          //     });
          //   }
          //   const callback = args[0];
          //   const depArray = args[1];
          //   if (!isGeneralFunctionExpression(callback)) {
          //     state.errors.push({
          //       message: `useEffect() first argument must be a function, received ${callback.type}`,
          //       node: callback.node,
          //       severity: "error",
          //     });
          //     continue;
          //   }
          //   if (!validateFunction(callback.node, state)) {
          //     continue;
          //   }
          //   const callbackParams = callback.get("params");
          //   if (callbackParams.length > 0) {
          //     state.errors.push({
          //       message: `useEffect() function must not have parameters, received ${callbackParams.length}`,
          //       node: callback.node,
          //       severity: "error",
          //     });
          //     continue;
          //   }

          //   const callbackBody = callback.get("body");
          //   if (!callbackBody.isBlockStatement()) {
          //     state.errors.push({
          //       message: `useEffect() function body must be a block statement, received ${callbackBody.type}`,
          //       node: callbackBody.node,
          //       severity: "error",
          //     });
          //     continue;
          //   }
          //   if (!depArray.isArrayExpression()) {
          //     state.errors.push({
          //       message: `useEffect() second argument must be an array, received ${depArray.type}`,
          //       node: depArray.node,
          //       severity: "error",
          //     });
          //     continue;
          //   }
          //   const depElements = depArray.get("elements");
          //   if (depElements.length > 0) {
          //     state.errors.push({
          //       message: `useEffect() dependency array must be empty, received ${depElements.length} elements`,
          //       node: depArray.node,
          //       severity: "warning",
          //     });
          //   }

          //   // let onUnmountHandlers: EventHandler | EventHandler[] | null = null;
          //   // const onMountHandlers = parseEventHandler(callbackBody, state, app, options, (returnPath) => {
          //   //   onUnmountHandlers = parseEventHandler(returnPath.get("argument") as NodePath<t.Expression>, state, app, options);
          //   // });
          //   continue;
          // }

          if (validateGlobalApi(_callee, "useImperativeHandle")) {
            var result = parseUseImperativeHandle(expr, templateRef, state, app, options);
            if (result) {
              var _component$children, _component$children2, _component$proxy;
              (_component$children = component.children) !== null && _component$children !== void 0 ? _component$children : component.children = [];
              (_component$children2 = component.children).push.apply(_component$children2, _toConsumableArray(result.components));
              (_component$proxy = component.proxy) !== null && _component$proxy !== void 0 ? _component$proxy : component.proxy = {};
              component.proxy.methods = result.methods;
            }
          }
          continue;
        }
      }
      if (!stmt.isTSInterfaceDeclaration() && !stmt.isTSTypeAliasDeclaration()) {
        state.errors.push({
          message: "Unsupported top level statement type: ".concat(stmt.type),
          node: stmt.node,
          severity: "error"
        });
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
  return component;
}
function parseReturnStatement(stmt, state, app, options) {
  var arg = stmt.get("argument");
  if (!arg.node) {
    state.errors.push({
      message: "Return statement in component function must have an argument",
      node: stmt,
      severity: "error"
    });
    return null;
  }
  return parseChildren(arg, state, app, options);
}
function parseIfStatementChildren(stmt, state, app, options) {
  if (stmt.isBlockStatement()) {
    var body = stmt.get("body");
    if (body.length === 0) {
      state.errors.push({
        message: "If statement block must contain a return statement",
        node: stmt.node,
        severity: "error"
      });
      return null;
    }
    var children = null;
    var _iterator6 = _createForOfIteratorHelper(body),
      _step6;
    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var st = _step6.value;
        var maybeChildren = parseIfStatementChildren(st, state, app, options);
        if (maybeChildren) {
          children = maybeChildren;
        }
      }
    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }
    return children;
  }
  if (stmt.isReturnStatement()) {
    return parseReturnStatement(stmt, state, app, options);
  }
  state.errors.push({
    message: "Only return statement is allowed in if statement, but received: ".concat(stmt.type),
    node: stmt.node,
    severity: "error"
  });
  return null;
}
//# sourceMappingURL=parseComponent.js.map